import React, { useState } from 'react';
import {
  FunnelIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  TrashIcon,
  CodeBracketIcon,
} from '@heroicons/react/24/outline';
import './ScriptHistorySidebar.css';

const ScriptHistorySidebar = ({ 
  scriptHistory = [], 
  currentScriptId, 
  onLoadScript, 
  onDeleteScript,
  onClose 
}) => {
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const categories = ['All Categories', 'Informatica', 'PySpark'];

  // Filter scripts based on selected category
  const filteredScripts = scriptHistory.filter(script => {
    if (selectedCategory === 'All Categories') return true;
    if (selectedCategory === 'Informatica' && script.metadata?.technology?.toLowerCase() === 'informatica') return true;
    if (selectedCategory === 'PySpark' && script.metadata?.technology?.toLowerCase() === 'pyspark') return true;
    if (selectedCategory === 'Work' && script.metadata?.name?.toLowerCase().includes('work')) return true;
    if (selectedCategory === 'Development' && script.metadata?.name?.toLowerCase().includes('development')) return true;
    return false;
  });

  const getIconForTechnology = (technology) => {
    const tech = technology?.toLowerCase();
    if (tech === 'informatica') return DocumentTextIcon;
    if (tech === 'pyspark') return CodeBracketIcon;
    return DocumentTextIcon;
  };

  const getIconBgColor = (technology) => {
    const tech = technology?.toLowerCase();
    if (tech === 'informatica') return 'bg-blue-500';
    if (tech === 'pyspark') return 'bg-green-500';
    return 'bg-gray-500';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };

  const handleScriptClick = (scriptId) => {
    if (onLoadScript) {
      onLoadScript(scriptId);
    }
  };

  const handleDeleteScript = (e, scriptId) => {
    e.stopPropagation();
    if (onDeleteScript) {
      onDeleteScript(scriptId);
    }
  };

  return (
    <div className="w-80 min-w-[320px] max-w-[350px] bg-white border-l border-gray-200 flex flex-col h-full script-history-sidebar">
      {/* Header with filter */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Script History</h2>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <FunnelIcon className="h-5 w-5 text-gray-600" />
          <div className="relative flex-1">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <span className="text-gray-900">{selectedCategory}</span>
              <ChevronDownIcon className="h-4 w-4 text-gray-600" />
            </button>
            
            {isDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => {
                      setSelectedCategory(category);
                      setIsDropdownOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 first:rounded-t-md last:rounded-b-md"
                  >
                    {category}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable history list */}
      <div className="flex-1 overflow-y-auto script-history-sidebar-list">
        <div className="p-4 space-y-3">
          {filteredScripts.length === 0 ? (
            <div className="text-center py-8">
              <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No scripts found</p>
              <p className="text-gray-400 text-xs mt-1">
                {selectedCategory === 'All Categories' 
                  ? 'Generate your first script to see it here' 
                  : `No scripts in ${selectedCategory} category`}
              </p>
            </div>
          ) : (
            filteredScripts.map((script) => {
              const IconComponent = getIconForTechnology(script.metadata?.technology);
              const iconBgColor = getIconBgColor(script.metadata?.technology);
              const isActive = currentScriptId === script.id;
              
              return (
                <div
                  key={script.id}
                  className={`bg-white rounded-lg border transition-all duration-200 p-4 cursor-pointer history-card ${
                    isActive 
                      ? 'border-blue-500 bg-blue-50 shadow-md' 
                      : 'border-gray-200 shadow-sm hover:shadow-md'
                  }`}
                  onClick={() => handleScriptClick(script.id)}
                >
                  <div className="flex items-start space-x-3">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full ${iconBgColor} flex items-center justify-center`}>
                      <IconComponent className="h-5 w-5 text-white" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {script.metadata?.name || 'Untitled Script'}
                        </h3>
                        <div className="flex items-center space-x-2">
                          <span className="flex-shrink-0 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                            {script.metadata?.technology || 'Unknown'}
                          </span>
                          <button
                            onClick={(e) => handleDeleteScript(e, script.id)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            title="Delete script"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="mt-1 text-xs text-gray-500 space-y-1">
                        <p className="text-gray-600 text-sm line-clamp-2">
                          {script.metadata?.totalLines || 0} lines • {script.metadata?.totalMappings || 0} mappings
                        </p>
                        <div className="flex items-center text-xs text-gray-500">
                          <CalendarIcon className="h-3 w-3 mr-1" />
                          {formatTimestamp(script.createdAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ScriptHistorySidebar;
